import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  createHealthRecordServiceClient,
  type Allergy,
  type AllergyInput,
  type HealthCondition,
  type HealthConditionInput,
} from "./client";

export type ClinicalRecordsStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useClinicalRecords() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.healthRecordService?.apiBaseUrl;
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [conditions, setConditions] = useState<HealthCondition[]>([]);
  const [status, setStatus] = useState<ClinicalRecordsStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createHealthRecordServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadClinicalRecords = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Clinical records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);
      setSaved(false);

      try {
        const [nextAllergies, nextConditions] = await Promise.all([
          client.listAllergies(signal),
          client.listConditions(signal),
        ]);
        setAllergies(nextAllergies);
        setConditions(nextConditions);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your clinical records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadClinicalRecords(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadClinicalRecords]);

  useEffect(() => {
    if (!saved) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setSaved(false), 3000);
    return () => window.clearTimeout(timeout);
  }, [saved]);

  const createAllergy = useCallback(
    async (payload: AllergyInput) => {
      if (!client) {
        setError("Clinical records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.createAllergy(payload);
        setAllergies((current) => [record, ...current]);
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the allergy record.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const updateAllergy = useCallback(
    async (allergyId: string, payload: Partial<AllergyInput>) => {
      if (!client) {
        setError("Clinical records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.updateAllergy(allergyId, payload);
        setAllergies((current) => current.map((item) => (item.allergy_id === allergyId ? record : item)));
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the allergy record.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const createCondition = useCallback(
    async (payload: HealthConditionInput) => {
      if (!client) {
        setError("Clinical records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.createCondition(payload);
        setConditions((current) => [record, ...current]);
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the known health condition.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const updateCondition = useCallback(
    async (conditionId: string, payload: Partial<HealthConditionInput>) => {
      if (!client) {
        setError("Clinical records are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);
      setSaved(false);

      try {
        const record = await client.updateCondition(conditionId, payload);
        setConditions((current) => current.map((item) => (item.condition_id === conditionId ? record : item)));
        setStatus("ready");
        setSaved(true);
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save the known health condition.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  return {
    allergies,
    conditions,
    createAllergy,
    createCondition,
    error,
    loadClinicalRecords,
    saved,
    status,
    updateAllergy,
    updateCondition,
  };
}
